import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMerch } from '../../contexts/MerchContext';
import { X, Trash2, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const CartDrawer = () => {
    const { isCartOpen, setIsCartOpen, cart, removeFromCart, updateQuantity, cartTotal } = useMerch();
    const navigate = useNavigate();
    const { user } = useAuth();

    if (!isCartOpen) return null;

    const handleCheckout = () => {
        setIsCartOpen(false);
        if (!user) {
            // Redirect to login with return url
            navigate('/user-login?redirect=/merch/checkout');
        } else {
            navigate('/merch/checkout');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={() => setIsCartOpen(false)}
            ></div>

            {/* Drawer */}
            <div className="relative w-full max-w-md bg-background border-l border-border h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
                <div className="p-6 border-b border-border flex items-center justify-between">
                    <h2 className="text-xl font-bold text-foreground flex items-center">
                        <ShoppingBag className="w-5 h-5 mr-2" />
                        Your Cart
                    </h2>
                    <button
                        onClick={() => setIsCartOpen(false)}
                        className="p-2 hover:bg-muted rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-muted-foreground" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {cart.length === 0 ? (
                        <div className="text-center py-12">
                            <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                            <p className="text-muted-foreground">Your cart is empty.</p>
                            <button
                                onClick={() => setIsCartOpen(false)}
                                className="mt-4 text-primary font-semibold hover:underline"
                            >
                                Continue Shopping
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {cart.map((item) => (
                                <div key={item.variant_id} className="flex gap-4">
                                    <div className="w-20 h-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                                        <img src={item.thumbnail_url} alt={item.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-foreground line-clamp-1">{item.name}</h4>
                                        <p className="text-sm text-muted-foreground mb-2">{item.variant_name}</p>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center border border-border rounded-lg">
                                                <button
                                                    onClick={() => updateQuantity(item.variant_id, item.quantity - 1)}
                                                    className="px-2 py-1 hover:bg-muted transition-colors"
                                                >-</button>
                                                <span className="px-2 text-sm font-medium">{item.quantity}</span>
                                                <button
                                                    onClick={() => updateQuantity(item.variant_id, item.quantity + 1)}
                                                    className="px-2 py-1 hover:bg-muted transition-colors"
                                                >+</button>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-semibold text-foreground">
                                                    ${(parseFloat(item.retail_price) * item.quantity).toFixed(2)}
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(item.variant_id)}
                                                    className="text-xs text-red-500 hover:text-red-400 mt-1 flex items-center justify-end w-full"
                                                >
                                                    <Trash2 className="w-3 h-3 mr-1" />
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {cart.length > 0 && (
                    <div className="p-6 border-t border-border bg-card">
                        <div className="flex justify-between items-center mb-4 text-lg font-bold">
                            <span className="text-foreground">Total</span>
                            <span className="text-primary">${cartTotal.toFixed(2)}</span>
                        </div>
                        <button
                            onClick={handleCheckout}
                            className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-bold font-lg hover:bg-primary/90 transition-all active:scale-[0.98]"
                        >
                            Checkout
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartDrawer;
